import { useState, useEffect, useRef } from 'react';
import { natsClient } from '../services/NATSClient';
import { getValidToken } from '../auth/tokenManager';
import { getShiftStreamName } from '../utils/tokenUtils';
import { getActiveOrgAlias } from '../services/demoMode';
import { isMachineRunning } from '../utils/machineStatus';
import { consumerOpts } from 'nats.ws';

export interface LiveMachineStatus {
  machineId: string;
  statusName: string;
  statusCode: number;
  timestamp: string;
  isRunning: boolean;
}

/**
 * Subscribe to live status transitions for all machines in a line.
 * Uses JetStream deliverLast to get current status immediately, then live updates.
 * Returns a map of machineId → latest status.
 */
export function useLiveLineStatus(machineIds: string[]): Map<string, LiveMachineStatus> {
  const [statuses, setStatuses] = useState<Map<string, LiveMachineStatus>>(new Map());
  const subsRef = useRef<any[]>([]);
  const activeRef = useRef(false);

  useEffect(() => {
    if (!machineIds.length) return;

    let cancelled = false;
    activeRef.current = true;

    const subscribe = async () => {
      // Wait for NATS to be connected
      if (!natsClient.isConnected()) {
        // Retry after a delay — NATS may still be connecting
        const retryTimer = setTimeout(() => {
          if (!cancelled) subscribe();
        }, 2000);
        return () => clearTimeout(retryTimer);
      }

      let streamName: string;
      try {
        const token = await getValidToken();
        if (cancelled) return;
        const orgAlias = getActiveOrgAlias(token);
        if (!orgAlias) return;
        // Live status transitions read edge-data.<serial>.shift.current.* which
        // now lives in the dedicated <ORG>_SHIFT stream.
        streamName = getShiftStreamName(orgAlias);
      } catch {
        return;
      }

      const js = natsClient.jetstream();

      // Subscribe to each machine's status transition subject
      for (const id of machineIds) {
        if (cancelled) break;
        const serial = id.padStart(5, '0');
        const subject = `edge-data.${serial}.shift.current.status.transition`;

        try {
          const opts = consumerOpts();
          opts.orderedConsumer();
          opts.filterSubject(subject);
          opts.deliverLast();
          opts.ackNone();
          (opts as any).stream = streamName;

          const sub = await js.subscribe(subject, opts);
          subsRef.current.push(sub);

          // Process messages in background
          (async () => {
            for await (const msg of sub) {
              if (cancelled) break;
              try {
                const parsed = JSON.parse(new TextDecoder().decode(msg.data));
                const transition = parsed.transition;
                if (!transition) continue;

                const status: LiveMachineStatus = {
                  machineId: id,
                  statusName: transition.to_name,
                  statusCode: transition.to,
                  timestamp: transition.at,
                  isRunning: isMachineRunning(transition.to_name, transition.to),
                };

                setStatuses(prev => {
                  const next = new Map(prev);
                  next.set(id, status);
                  return next;
                });
              } catch { /* ignore parse errors */ }
            }
          })();
        } catch {
          // Machine may not have data on this stream — skip
        }
      }
    };

    subscribe();

    return () => {
      cancelled = true;
      activeRef.current = false;
      for (const sub of subsRef.current) {
        try { sub.unsubscribe(); } catch { /* already closed */ }
      }
      subsRef.current = [];
    };
  }, [machineIds.join(',')]);

  return statuses;
}
